/**
 * @file A utility for interacting with the Australian Business Register (ABR) API.
 * It handles lookups by ABN/ACN and by name.
 */

// It's a best practice to get the GUID from environment variables.
const ABR_GUID = '3c3dc6bc-8277-448c-bf56-fafef1809e6c';
const ABR_BASE_URL = 'https://abr.business.gov.au/json/';

/**
 * Performs the core JSONP request to the ABR API.
 * This is a private helper function not meant to be exported directly.
 * @param {string} url - The full URL for the ABR endpoint.
 * @returns {Promise<object>} A promise that resolves with the lookup data.
 * @private
 */
const _performJsonpRequest = (url) => {
    return new Promise((resolve, reject) => {
        if (!ABR_GUID) {
            const errorMsg = "Configuration Error: VUE_APP_ABN_LOOKUP_GUID is not set in your .env file.";
            console.error(errorMsg);
            return reject(new Error(errorMsg));
        }

        // Create a unique callback name to avoid conflicts with concurrent requests.
        const callbackName = `jsonpCallback_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        const script = document.createElement('script');

        // The ABR API requires the callback and GUID to be in the query string.
        script.src = `${url}&callback=${callbackName}&guid=${ABR_GUID}`;

        // The ABR service will invoke this global function upon returning data.
        window[callbackName] = (data) => {
            resolve(data); // Resolve with the data payload.

            // Cleanup the script tag and the global callback function.
            document.body.removeChild(script);
            delete window[callbackName];
        };

        // Handle network errors or issues loading the script.
        script.onerror = (err) => {
            const errorMsg = 'Network Error: Failed to load ABR lookup script.';
            console.error(errorMsg, err);
            reject(new Error(errorMsg));

            // Perform cleanup even on error.
            document.body.removeChild(script);
            delete window[callbackName];
        };

        document.body.appendChild(script);
    });
};

/**
 * Looks up an entity by its ABN (11 digits) or ACN (9 digits).
 * @param {string | number} number - The ABN or ACN to look up.
 * @returns {Promise<object>} A promise that resolves with the entity details.
 * @example
 * const details = await lookupByNumber('12345678901');
 * if (details.EntityName) { console.log(details.EntityName); }
 */
export const lookupByNumber = (number) => {
    const sanitizedNumber = String(number).replace(/[^\d]/g, '');
    const numberLength = sanitizedNumber.length;

    if (numberLength !== 9 && numberLength !== 11) {
        return Promise.reject(new Error('Invalid input: Number must be 9 (ACN) or 11 (ABN) digits.'));
    }

    const isAbn = numberLength === 11;
    const endpoint = isAbn ? `AbnDetails.aspx?abn=${sanitizedNumber}` : `AcnDetails.aspx?acn=${sanitizedNumber}`;
    const url = `${ABR_BASE_URL}${endpoint}`;

    return _performJsonpRequest(url);
};

/**
 * Searches for businesses or companies by name.
 * @param {string} name - The name to search for (must be at least 3 characters).
 * @param {object} [filters={}] - Optional filters for the search.
 * @param {string} [filters.stateCode] - e.g., 'QLD', 'NSW', 'VIC'.
 * @param {string} [filters.postcode] - e.g., '4000', '2000'.
 * @returns {Promise<object>} A promise that resolves with a list of matching names.
 * @example
 * const results = await lookupByName('Test Company', { stateCode: 'NSW' });
 * if (results.Names) { results.Names.forEach(name => console.log(name.Abn)); }
 */
export const lookupByName = (name, filters = {}) => {
    if (!name || String(name).trim().length < 3) {
        return Promise.reject(new Error('Invalid input: Search name must be at least 3 characters.'));
    }

    // Construct the query string for the name search.
    const queryParams = new URLSearchParams({ name, ...filters });
    const endpoint = `NameSearch.aspx?${queryParams.toString()}`;
    const url = `${ABR_BASE_URL}${endpoint}`;

    return _performJsonpRequest(url);
};


// WEBPACK FOOTER //
// ./src/utils/abnAcnUtils.js